import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, FC } from "react";
import { pad, reducedMotion } from "./hooks";
import type { Film as FilmData, Piece, Shelf } from "./works";

/** A case study's film: it plays, silent, for as long as it is on screen. */
export const Film: FC<{ film: FilmData }> = ({ film }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const still = reducedMotion();

  useEffect(() => {
    const video = ref.current;
    if (!video || still) return;
    const watch = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) void video.play().catch(() => undefined);
        else video.pause();
      },
      { threshold: 0.2 }
    );
    watch.observe(video);
    return () => watch.disconnect();
  }, [still]);

  return (
    <section
      className="sd-film"
      data-upright={film.upright || undefined}
      style={{ "--ratio": film.ratio } as CSSProperties}
    >
      <video
        ref={ref}
        src={film.src}
        poster={film.poster}
        muted={muted}
        controls={still}
        loop
        playsInline
        preload="metadata"
      />
      <p className="sd-bench-tag">{film.label}</p>
      <button type="button" className="sd-film-sound" aria-pressed={!muted} onClick={() => setMuted(m => !m)}>
        sound {muted ? "off" : "on"}
      </button>
    </section>
  );
};

interface TileProps {
  piece: Piece;
  n: number;
  /** Load the video while the pointer is on the tile. */
  hover: boolean;
  onOpen: () => void;
}

const Tile: FC<TileProps> = ({ piece, n, hover, onOpen }) => {
  const [live, setLive] = useState(false);

  return (
    <li>
      <button
        type="button"
        aria-label={`open ${piece.title}`}
        onClick={onOpen}
        onPointerEnter={() => setLive(true)}
        onPointerLeave={() => setLive(false)}
      >
        <span className="sd-piece-pic">
          <img className="sd-cover" src={piece.poster} alt={piece.title} loading="lazy" />
          {live && hover && piece.video && (
            <video className="sd-cover" src={piece.video} autoPlay muted loop playsInline />
          )}
        </span>
        <span className="sd-piece-name">
          <span>{pad(n + 1)}</span>
          <span>{piece.title}</span>
        </span>
      </button>
    </li>
  );
};

/** What the viewer needs of a work; a clip in the studio gallery has no poster. */
export type Shown = Omit<Piece, "poster"> & { poster?: string };

interface ViewerProps {
  pieces: Shown[];
  at: number;
  onStep: (by: number) => void;
  onClose: () => void;
}

/** One work at full size over the page; the arrow keys step, escape closes. */
export const Viewer: FC<ViewerProps> = ({ pieces, at, onStep, onClose }) => {
  const close = useRef<HTMLButtonElement>(null);
  const piece = pieces[at];

  useEffect(() => {
    close.current?.focus();
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onStep(1);
      if (e.key === "ArrowLeft") onStep(-1);
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose, onStep]);

  // Drawn at the root of the page so no section can stack above it, the nav included.
  return createPortal(
    <div className="sd-viewer" role="dialog" aria-modal aria-label={piece.title} onClick={onClose}>
      <figure className="sd-viewer-box" onClick={e => e.stopPropagation()}>
        <button ref={close} className="sd-viewer-x" type="button" onClick={onClose} aria-label="Close">
          ×
        </button>
        {piece.video ? (
          <video key={piece.video} src={piece.video} poster={piece.poster} controls autoPlay loop playsInline />
        ) : (
          <img key={piece.title} src={piece.full ?? piece.poster} alt={piece.title} />
        )}
        <figcaption className="sd-viewer-bar">
          <button type="button" onClick={() => onStep(-1)} aria-label="Previous">
            ←
          </button>
          <p>
            {pad(at + 1)} / {pad(pieces.length)} / {piece.title}
          </p>
          <button type="button" onClick={() => onStep(1)} aria-label="Next">
            →
          </button>
        </figcaption>
      </figure>
    </div>,
    document.querySelector(".sd") ?? document.body,
  );
};

/** One series of a case study as a row of tiles; a tile opens the work at full size. */
export const Gallery: FC<{ shelf: Shelf }> = ({ shelf }) => {
  const [at, setAt] = useState<number | null>(null);
  const count = shelf.pieces.length;
  const step = useCallback((by: number) => setAt(n => (n === null ? n : (n + by + count) % count)), [count]);
  const close = useCallback(() => setAt(null), []);

  return (
    <section id={shelf.id} className="sd-gallery">
      <header className="sd-cases-head">
        <p className="sd-label">{shelf.label}</p>
        <h2>{shelf.title}</h2>
        <p className="sd-label">{shelf.of ? `${pad(count)} of ${shelf.of}` : `${pad(count)} / ${pad(count)}`}</p>
      </header>
      <ol className="sd-pieces" style={{ "--ratio": shelf.ratio, "--cols": shelf.cols } as CSSProperties}>
        {shelf.pieces.map((piece, n) => (
          <Tile key={piece.poster} piece={piece} n={n} hover={!shelf.animated} onOpen={() => setAt(n)} />
        ))}
      </ol>
      <a className="sd-more" href={shelf.more} target="_blank" rel="noreferrer">
        {shelf.of ? `see all ${shelf.of} on orkhan.art ↗` : "see the series on orkhan.art ↗"}
      </a>
      {at !== null && <Viewer pieces={shelf.pieces} at={at} onStep={step} onClose={close} />}
    </section>
  );
};
